import React, { useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import { API } from '../config';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import AnalisiMap from '../components/analisi/AnalisiMap';
import { MobileTheme } from './mobileTheme';
import { IconFilter } from '../components/Icons';

const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('it-IT'));

// colori mappa adattivi al tema: sagoma più scura dello sfondo in modo chiaro,
// un filo più chiara in modo scuro, così si distingue sempre dal fondo pagina.
function _parseCol(s) {
  if (!s) return null;
  s = s.trim();
  if (s[0] === '#') {
    let h = s.slice(1);
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (m) { const p = m[1].split(',').map((x) => parseInt(x, 10)); return [p[0], p[1], p[2]]; }
  return null;
}
function _shade(rgb, t) {
  return rgb.map((c) => Math.round(t < 0 ? c * (1 + t) : c + (255 - c) * t));
}
function mappaColori(mode) {
  const el = typeof document !== 'undefined' ? document.querySelector('.m-app') : null;
  const bg = el ? getComputedStyle(el).getPropertyValue('--bg') : '';
  const rgb = _parseCol(bg) || (mode === 'light' ? [235, 245, 241] : [10, 22, 38]);
  const dir = mode === 'light' ? -1 : 1;   // chiaro -> più scuro; scuro -> più chiaro
  const base = _shade(rgb, dir * 0.11);
  const stroke = _shade(rgb, dir * 0.30);
  return { baseFill: `rgb(${base.join(',')})`, stroke: `rgb(${stroke.join(',')})`, rampLo: base };
}
const TEC_COL = { FTTH: '#2563eb', FTTC: '#0ea5e9', FWA: '#a855f7', ADSL: '#94a3b8' };
const cnt = (v) => nf(Math.round(v));
const pct = (v) => `${v.toFixed(0)}%`;
const METRICS = [
  { key: 'lead_caldi', short: 'Lead caldi', base: [16, 160, 80], fmt: cnt, value: (d) => d.lead_caldi },
  { key: 'ftth', short: 'FTTH', base: [37, 99, 235], fmt: cnt, value: (d) => d.ftth },
  { key: 'fwa', short: 'FWA', base: [168, 85, 247], fmt: cnt, value: (d) => d.fwa },
  { key: 'fttc', short: 'FTTC', base: [14, 165, 233], fmt: cnt, value: (d) => d.fttc },
  { key: 'futuri', short: 'Futuri', base: [245, 158, 11], fmt: cnt, value: (d) => d.futuri },
  { key: 'scoperti', short: 'Scoperti', base: [234, 88, 12], fmt: cnt, value: (d) => d.scoperti },
  { key: 'pftth', short: '% FTTH', base: [29, 78, 216], fmt: pct, value: (d) => (d.coperti ? (d.ftth / d.coperti) * 100 : 0) },
  { key: 'vodafone', short: 'Vodafone', base: [230, 0, 0], fmt: cnt, value: (d) => d.vodafone },
];

function Mappa() {
  const { theme, mode } = useContext(MobileTheme);
  const mapCols = useMemo(() => mappaColori(mode), [theme, mode]);
  const [data, setData] = useState(null);
  const [metricKey, setMetricKey] = useState('lead_caldi');
  const [drillRegion, setDrillRegion] = useState(null);   // regione zoomata sulla mappa
  const [filterOpen, setFilterOpen] = useState(false);    // finestra filtri (metrica)
  const [provWin, setProvWin] = useState(false);          // finestra dettaglio province
  const [mapProv, setMapProv] = useState(null);           // provincia toccata sulla mappa (aggiorna i contatori sotto)
  const [selProv, setSelProv] = useState(null);
  const [comuni, setComuni] = useState(null);

  useEffect(() => { axios.get(`${API}/analisi/geo`).then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!selProv) { setComuni(null); return; }
    axios.get(`${API}/analisi/comuni`, { params: { provincia: selProv, metric: (metricKey === 'pftth' ? 'ftth' : metricKey), limit: 8 } })
      .then((r) => setComuni(r.data?.comuni || [])).catch(() => setComuni([]));
  }, [selProv, metricKey]);

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  const reg = drillRegion && data ? data.regioni[drillRegion] : null;
  const cur = (mapProv && data) ? data.province[mapProv] : reg;   // oggetto dei contatori sotto: provincia (se toccata) o regione
  const tornaItalia = () => { setDrillRegion(null); setMapProv(null); setProvWin(false); setSelProv(null); };
  const provList = (drillRegion && data)
    ? Object.entries(data.province).filter(([, v]) => v.reg === drillRegion)
        .map(([k, v]) => ({ key: k, ...v }))
        .sort((a, b) => metric.value(b) - metric.value(a))
    : [];
  const maxProv = provList.length ? Math.max(1, ...provList.map((p) => metric.value(p))) : 1;
  const maxC = comuni && comuni.length ? Math.max(1, ...comuni.map((c) => metric.value(c))) : 1;
  const timAttivi = cur ? (cur.tim - (cur.tim_inattivi || 0)) : 0;
  const nonTimEst = cur ? (cur.nontim + (cur.tim_inattivi || 0)) : 0;

  return (
    <>
      <div style={{ position: 'relative', height: 420, overflow: 'hidden' }}>
        <AnalisiMap data={data} metric={metric} drillRegion={drillRegion} selectedProv={mapProv} zoom={false}
          bg="transparent" baseFill={mapCols.baseFill} stroke={mapCols.stroke} rampLo={mapCols.rampLo} baseOpacity={0.92}
          onRegionClick={(k) => { setDrillRegion(k); setMapProv(null); }}
          onProvinceClick={(k) => setMapProv((prev) => (prev === k ? null : k))} />
        {drillRegion && (
          <button className="m-map-back" onClick={tornaItalia} aria-label="Torna all'Italia">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        )}
        <button className="m-map-filter" onClick={() => setFilterOpen(true)} aria-label="Filtro metrica">
          <IconFilter size={15} /><span>{metric.short}</span>
        </button>
      </div>

      {reg && (
        <div style={{ marginTop: 10 }}>
          <div className="m-bcrumb">
            <b style={{ textTransform: 'capitalize' }}>{(drillRegion || '').toLowerCase()}</b>
            {mapProv && <><span>›</span><b style={{ textTransform: 'capitalize' }}>{(mapProv || '').toLowerCase()}</b></>}
            <span className="hint">{mapProv ? 'ri-tocca per la regione' : 'tocca una provincia'}</span>
          </div>
          <div className="m-chips">
            <div className="m-chip"><div className="cn">{nf(cur.totale)}</div><div className="cl">Caricati</div></div>
            <div className="m-chip"><div className="cn">{nf(timAttivi)}</div><div className="cl">TIM</div></div>
            <div className="m-chip k-non"><div className="cn">{nf(nonTimEst)}</div><div className="cl">NON TIM</div></div>
            <div className="m-chip k-lead"><div className="cn">{nf(cur.lead_caldi)}</div><div className="cl">Lead caldi</div></div>
            <div className="m-chip k-cop"><div className="cn">{nf(cur.ftth)}</div><div className="cl">FTTH</div></div>
            <div className="m-chip"><div className="cn">{nf(cur.fwa)}</div><div className="cl">FWA</div></div>
            <div className="m-chip"><div className="cn">{nf(cur.futuri)}</div><div className="cl">Futuri FTTH</div></div>
            <div className="m-chip"><div className="cn">{nf(cur.scoperti)}</div><div className="cl">Scoperti</div></div>
          </div>
          {cur.tim_inattivi > 0 && (
            <div className="m-sub" style={{ marginTop: 6, padding: 0 }}>NON TIM include {nf(cur.tim_inattivi)} TIM non attivi</div>
          )}
          <button onClick={() => { setSelProv(null); setProvWin(true); }}
            style={{ width: '100%', marginTop: 12, padding: '10px', borderRadius: 10, border: '1px solid var(--line)', background: 'var(--surface2)', color: 'var(--ink)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Dettaglio province
          </button>
        </div>
      )}

      {filterOpen && (
        <div className="m-win-bg" onClick={() => setFilterOpen(false)}>
          <div className="m-win" onClick={(e) => e.stopPropagation()}>
            <div className="m-win-h">
              <button className="bk" onClick={() => setFilterOpen(false)} aria-label="Indietro">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <h2>Metrica</h2>
            </div>
            <div className="m-win-b">
              <div className="m-mchips">
                {METRICS.map((m) => (
                  <button key={m.key} onClick={() => { setMetricKey(m.key); setFilterOpen(false); }}
                    className={metricKey === m.key ? 'on' : ''}
                    style={metricKey === m.key ? { background: `rgb(${m.base})`, borderColor: `rgb(${m.base})`, color: '#fff' } : undefined}>
                    {m.short}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {provWin && reg && (
        <div className="m-win-bg" onClick={() => { setProvWin(false); setSelProv(null); }}>
          <div className="m-win" onClick={(e) => e.stopPropagation()}>
            <div className="m-win-h">
              <button className="bk" onClick={() => { if (selProv) setSelProv(null); else setProvWin(false); }} aria-label="Indietro">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <h2 style={{ textTransform: 'capitalize' }}>{selProv ? (selProv || '').toLowerCase() : `${(drillRegion || '').toLowerCase()} · province`}</h2>
            </div>
            <div className="m-win-b">
              {!selProv ? provList.map((p) => {
                const v = metric.value(p); const pc = Math.round((v / maxProv) * 100);
                return (
                  <button key={p.key} onClick={() => setSelProv(p.key)}
                    style={{ width: '100%', background: 'none', border: 'none', padding: '7px 2px', cursor: 'pointer', textAlign: 'left', color: 'var(--ink)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                      <span style={{ textTransform: 'capitalize' }}>{(p.key || '').toLowerCase()}</span><b>{metric.fmt(v)}</b>
                    </div>
                    <div style={{ height: 6, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pc}%`, height: '100%', background: `rgb(${metric.base})` }} />
                    </div>
                  </button>
                );
              }) : (
                <>
                  <div className="m-sub" style={{ padding: 0, marginBottom: 4 }}>Top comuni · {metric.short}</div>
                  {comuni && comuni.length > 0 ? comuni.map((c) => {
                    const cv = metric.value(c); const cpc = Math.round((cv / maxC) * 100);
                    return (
                      <div key={c.comune} style={{ padding: '4px 2px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                          <span style={{ textTransform: 'capitalize' }}>{(c.comune || '').toLowerCase()}</span><b>{metric.fmt(cv)}</b>
                        </div>
                        <div style={{ height: 5, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${cpc}%`, height: '100%', background: `rgb(${metric.base})` }} />
                        </div>
                      </div>
                    );
                  }) : <div className="m-sub" style={{ padding: 0 }}>Nessun dato comuni.</div>}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Velocita() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/analisi/velocita`).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="m-empty">Caricamento…</div>;
  const TECS = ['FTTH', 'FTTC', 'FWA', 'ADSL'];
  const maxDown = Math.max(1, ...d.per_tecnologia.map((t) => t.down_max || 0));
  return (
    <>
      <div className="m-secrow"><h2>Velocità per tecnologia</h2></div>
      {d.per_tecnologia.map((t) => (
        <div key={t.tec} style={{ padding: '7px 2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: TEC_COL[t.tec] || '#94a3b8' }} />
            <b>{t.tec}</b>
            <span style={{ marginLeft: 'auto' }}><b>{nf(t.down_avg)}</b> <span style={{ color: 'var(--muted)' }}>↓ · {nf(t.up_avg)} ↑ · {nf(t.n)} linee</span></span>
          </div>
          <div style={{ height: 6, background: 'var(--line)', borderRadius: 4, overflow: 'hidden', marginTop: 5 }}>
            <div style={{ width: `${Math.round((t.down_avg / maxDown) * 100)}%`, height: '100%', background: TEC_COL[t.tec] || '#94a3b8' }} />
          </div>
        </div>
      ))}

      <div className="m-secrow"><h2>Download vs Upload</h2></div>
      <div style={{ height: 300, background: 'var(--surface)', borderRadius: 14, padding: 8 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 6, right: 10, bottom: 18, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,.2)" />
            <XAxis type="number" dataKey="down" name="Down" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
            <YAxis type="number" dataKey="up" name="Up" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
            <Tooltip content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const p = payload[0].payload;
              return <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 8px', fontSize: 11, color: '#0f172a' }}><b>{p.tec}</b> · {p.prov}<br />↓ {nf(p.down)} · ↑ {nf(p.up)}</div>;
            }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {TECS.map((t) => (
              <Scatter key={t} name={t} data={d.punti.filter((p) => p.tec === t)} fill={TEC_COL[t]} fillOpacity={0.6} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

function Futuri() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/analisi/futuri-ftth`).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="m-empty">Caricamento…</div>;
  const TC = { FTTC: '#0ea5e9', FWA: '#a855f7', ADSL: '#94a3b8' };
  const maxProv = Math.max(1, ...d.top_province.map((p) => p.futuri));
  return (
    <>
      <div className="m-secrow"><h2>Candidati a FTTH</h2></div>
      <div className="m-chips">
        {d.per_tec.map((t) => (
          <div key={t.tec} className="m-chip"><div className="cn" style={{ color: TC[t.tec] }}>{nf(t.n)}</div><div className="cl">{t.tec} · {nf(t.nontim)} NT</div></div>
        ))}
      </div>
      <div className="m-secrow"><h2>Top province</h2></div>
      {d.top_province.map((p) => {
        const w = Math.round((p.futuri / maxProv) * 100);
        return (
          <div key={p.prov} style={{ padding: '5px 2px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
              <span style={{ textTransform: 'capitalize' }}>{(p.prov || '').toLowerCase()}</span><b>{nf(p.futuri)}</b>
            </div>
            <div style={{ height: 6, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${w}%`, height: '100%', background: '#f59e0b' }} />
            </div>
          </div>
        );
      })}
      <div className="m-secrow"><h2>Diventati FTTH{d.diventati_tot ? ` · ${nf(d.diventati_tot)}` : ''}</h2></div>
      {d.diventati_tot === 0 ? (
        <div className="m-empty" style={{ padding: '20px 16px' }}>Nessuno ancora. Appariranno qui quando un FTTC/FWA/ADSL passa a fibra.</div>
      ) : d.diventati.map((x, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '6px 2px', borderBottom: '1px solid var(--line)' }}>
          <span style={{ textTransform: 'capitalize' }}>{(x.nome || x.numero || '').toString().toLowerCase()}</span>
          <span style={{ color: 'var(--muted)' }}>{x.prov}</span>
        </div>
      ))}
    </>
  );
}

export default function AnalisiPage() {
  const [tab, setTab] = useState('mappa');
  return (
    <>
      <div className="m-seg2" style={{ marginBottom: 12 }}>
        <button className={tab === 'mappa' ? 'on' : ''} onClick={() => setTab('mappa')}>Mappa</button>
        <button className={tab === 'futuri' ? 'on' : ''} onClick={() => setTab('futuri')}>Futuri</button>
        <button className={tab === 'velocita' ? 'on' : ''} onClick={() => setTab('velocita')}>Qualità</button>
      </div>
      {tab === 'mappa' ? <Mappa /> : tab === 'futuri' ? <Futuri /> : <Velocita />}
    </>
  );
}
